import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SpaceScene from '../components/SpaceScene';
import Navigation from '../components/Navigation';

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? window.scrollY / max : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return progress;
}

function rangeOpacity(p, fadeIn, full, fadeOut, end) {
  if (p <= fadeIn) return 0;
  if (p < full) return (p - fadeIn) / (full - fadeIn);
  if (p <= fadeOut) return 1;
  if (p < end) return 1 - (p - fadeOut) / (end - fadeOut);
  return 0;
}

function ServiceItem({ title, desc, icon, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.4, delay, ease: [0.23, 1, 0.32, 1] }}
      className="flex flex-col items-center text-center"
      style={{ maxWidth: 220 }}
    >
      <svg
        width="36"
        height="36"
        viewBox="0 0 40 40"
        fill="none"
        stroke="rgba(157,76,221,0.35)"
        strokeWidth="0.8"
        className="mb-5"
      >
        {icon}
      </svg>
      <span
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.85rem',
          fontWeight: 500,
          letterSpacing: '0.08em',
          color: 'rgba(255,255,255,0.8)',
          marginBottom: 8,
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: 1.7,
          color: 'rgba(255,255,255,0.3)',
        }}
      >
        {desc}
      </span>
    </motion.div>
  );
}

export default function LandingPage() {
  const p = useScrollProgress();

  const heroOp    = p <= 0 ? 1 : rangeOpacity(p, -0.01, 0.001, 0.06, 0.12);
  const taglineOp = rangeOpacity(p, 0.16, 0.22, 0.33, 0.40);
  const labOp     = rangeOpacity(p, 0.52, 0.58, 0.82, 0.88);
  // CTA is the final section: stay fully visible through the bottom of the page.
  const ctaOp     = rangeOpacity(p, 0.88, 0.92, 1.01, 1.02);

  // "Scroll to explore" hint: appears shortly after load, fades out as soon as they scroll.
  const [hintReady, setHintReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setHintReady(true), 1500);
    return () => clearTimeout(t);
  }, []);
  const hintOp = !hintReady ? 0 : p <= 0.01 ? 1 : p >= 0.04 ? 0 : 1 - (p - 0.01) / 0.03;

  return (
    <>
      <SpaceScene />

      {/* Shooting stars */}
      <div className="shooting-star shooting-star-1" />
      <div className="shooting-star shooting-star-2" />
      <div className="shooting-star shooting-star-3" />

      <Navigation />

      {/* Scroll height for camera movement */}
      <div
        data-testid="scroll-container"
        style={{ height: '600vh', position: 'relative', zIndex: 1, pointerEvents: 'none' }}
      />

      {/* ═══ HERO — empty, just the space scene ═══ */}
      <div
        data-testid="hero-overlay"
        className="fixed inset-0"
        style={{ zIndex: 10, opacity: heroOp, pointerEvents: 'none' }}
      />

      {/* ═══ SCROLL HINT ═══ */}
      <div
        data-testid="scroll-hint"
        className="fixed left-0 right-0 flex flex-col items-center"
        style={{
          bottom: 36,
          zIndex: 10,
          opacity: hintOp,
          transition: 'opacity 1.2s ease',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.7rem',
            fontWeight: 500,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
            marginBottom: 14,
          }}
        >
          Scroll to explore
        </span>
        <div className="scroll-hint-line" />
      </div>

      {/* ═══ TAGLINE ═══ */}
      <div
        data-testid="tagline-overlay"
        className="fixed inset-0 flex items-center justify-center"
        style={{ zIndex: 10, opacity: taglineOp, pointerEvents: 'none' }}
      >
        <h2
          className="text-glow text-center px-8"
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 'clamp(1.8rem, 4.5vw, 3.8rem)',
            fontWeight: 600,
            letterSpacing: '0.02em',
            lineHeight: 1.2,
            color: 'rgba(255,255,255,0.92)',
          }}
        >
          Bringing Intelligence to Life
        </h2>
      </div>

      {/* ═══ IN THE LAB ═══ */}
      <div
        data-testid="lab-overlay"
        className="fixed inset-0 flex flex-col items-center justify-center"
        style={{ zIndex: 10, opacity: labOp, pointerEvents: 'none' }}
      >
        <span
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 'clamp(0.65rem, 1.1vw, 0.8rem)',
            fontWeight: 500,
            letterSpacing: '0.2em',
            color: 'rgba(157,76,221,0.55)',
            textTransform: 'uppercase',
            marginBottom: 48,
          }}
        >
          In the Lab
        </span>
        <div className="flex gap-16 md:gap-28 flex-wrap justify-center px-8">
          <ServiceItem
            title="Development"
            desc="We build intelligent systems that scale."
            delay={0}
            icon={
              <>
                <rect x="8" y="8" width="24" height="24" rx="2" />
                <line x1="14" y1="16" x2="26" y2="16" />
                <line x1="14" y1="20" x2="22" y2="20" />
                <line x1="14" y1="24" x2="18" y2="24" />
              </>
            }
          />
          <ServiceItem
            title="Strategy"
            desc="We turn complexity into clear direction."
            delay={0.15}
            icon={
              <>
                <circle cx="20" cy="20" r="12" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="20" y1="14" x2="26" y2="20" />
                <circle cx="20" cy="20" r="2" fill="rgba(157,76,221,0.35)" />
              </>
            }
          />
          <ServiceItem
            title="Consulting"
            desc="We apply AI where it actually matters."
            delay={0.3}
            icon={
              <>
                <circle cx="20" cy="14" r="6" />
                <path d="M10 32 C10 24 30 24 30 32" />
              </>
            }
          />
        </div>
      </div>

      {/* ═══ CTA ═══ */}
      <div
        data-testid="cta-overlay"
        className="fixed inset-0 flex flex-col items-center justify-center"
        style={{ zIndex: 10, opacity: ctaOp, pointerEvents: ctaOp > 0.1 ? 'auto' : 'none' }}
      >
        <h2
          className="text-glow text-center px-8 mb-10"
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 'clamp(1.4rem, 3vw, 2.4rem)',
            fontWeight: 500,
            letterSpacing: '0.02em',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          Let's Build Something Meaningful
        </h2>
        <a
          href="https://calendly.com/sanshalabs"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="book-call-btn"
          className="cta-button"
        >
          Book a Call
        </a>
        <a
          href="https://www.linkedin.com/company/sansha-labs"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="linkedin-link"
          aria-label="Sansha Labs on LinkedIn"
          className="social-link mt-8"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
          </svg>
          <span>LinkedIn</span>
        </a>
      </div>
    </>
  );
}
